import { useState, useCallback, useEffect, useRef } from 'react';
import { type GoalTemplate } from './goalTemplates';
import { SK_ACTIVE_GOALS } from './constants/storageKeys';
import { warmBackend } from './auth';

export interface CustomStep {
  id: string;
  label: string;
}

export interface ActiveGoal {
  id: string;
  templateId: string;
  adoptedAt: string;
  completedSteps: string[];
  completedAt?: string;
  xpAwarded?: boolean;
  isCustom?: boolean;
  customTitle?: string;
  customDescription?: string;
  customSteps?: CustomStep[];
  /** Server-side row ID for API persistence */
  serverId?: number;
}

const API_BASE = '/api';

function loadGoals(): ActiveGoal[] {
  try {
    const raw = localStorage.getItem(SK_ACTIVE_GOALS);
    if (raw) return JSON.parse(raw) as ActiveGoal[];
  } catch { /* error handled silently */ }
  return [];
}

function saveGoalsLocal(goals: ActiveGoal[]) {
  try {
    localStorage.setItem(SK_ACTIVE_GOALS, JSON.stringify(goals));
  } catch { /* error handled silently */ }
}

async function apiRequest<T>(method: string, path: string, token: string, body?: unknown): Promise<T | null> {
  try {
    await warmBackend();
    const headers: HeadersInit = { 'Authorization': `Bearer ${token}` };
    const opts: RequestInit = { method, headers, credentials: 'include' };
    if (body !== undefined) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    const res = await fetch(`${API_BASE}${path}`, opts);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export function useGoals(authToken?: string | null) {
  const [goals, setGoals] = useState<ActiveGoal[]>(loadGoals);
  const initialLoadDone = useRef(false);

  // Load goals from API on mount when authenticated
  useEffect(() => {
    if (!authToken || initialLoadDone.current) return;
    initialLoadDone.current = true;

    (async () => {
      const serverGoals = await apiRequest<Array<{
        id: number;
        templateId: string;
        isCustom: boolean;
        customTitle: string | null;
        customDescription: string | null;
        customSteps: CustomStep[] | null;
        completedSteps: string[] | null;
        xpAwarded: boolean;
        adoptedAt: string;
        completedAt: string | null;
      }>>('GET', '/goals', authToken);

      if (serverGoals && serverGoals.length > 0) {
        const mapped: ActiveGoal[] = serverGoals.map(sg => ({
          id: `server-goal-${sg.id}`,
          serverId: sg.id,
          templateId: sg.templateId,
          adoptedAt: sg.adoptedAt,
          completedSteps: sg.completedSteps ?? [],
          completedAt: sg.completedAt ?? undefined,
          xpAwarded: sg.xpAwarded,
          isCustom: sg.isCustom,
          customTitle: sg.customTitle ?? undefined,
          customDescription: sg.customDescription ?? undefined,
          customSteps: sg.customSteps ?? undefined,
        }));
        setGoals(mapped);
        saveGoalsLocal(mapped);
      } else {
        // Migrate localStorage goals to server
        const localGoals = loadGoals();
        if (localGoals.length > 0) {
          for (const g of localGoals) {
            const result = await apiRequest<{ id: number }>('POST', '/goals', authToken, {
              templateId: g.templateId,
              isCustom: g.isCustom ?? false,
              customTitle: g.customTitle,
              customDescription: g.customDescription,
              customSteps: g.customSteps,
            });
            if (result) {
              g.serverId = result.id;
              // Sync steps if any
              if (g.completedSteps.length > 0 || g.completedAt) {
                await apiRequest('PUT', `/goals/${result.id}/steps`, authToken, {
                  completedSteps: g.completedSteps,
                  completedAt: g.completedAt ?? null,
                });
              }
              if (g.xpAwarded) {
                await apiRequest('PUT', `/goals/${result.id}/xp`, authToken, {});
              }
            }
          }
          setGoals([...localGoals]);
          saveGoalsLocal(localGoals);
        }
      }
    })();
  }, [authToken]);

  const adoptGoal = useCallback((template: GoalTemplate): boolean => {
    let alreadyAdopted = false;
    setGoals(prev => {
      if (prev.some(g => g.templateId === template.id && !g.completedAt)) {
        alreadyAdopted = true;
        return prev;
      }
      const newGoal: ActiveGoal = {
        id: `goal-${Date.now()}`,
        templateId: template.id,
        adoptedAt: new Date().toISOString(),
        completedSteps: [],
      };
      const next = [...prev, newGoal];
      saveGoalsLocal(next);

      // Persist to API
      if (authToken) {
        apiRequest<{ id: number }>('POST', '/goals', authToken, {
          templateId: template.id,
        }).then(result => {
          if (result) {
            setGoals(p => {
              const updated = p.map(g => g.id === newGoal.id ? { ...g, serverId: result.id } : g);
              saveGoalsLocal(updated);
              return updated;
            });
          }
        });
      }

      return next;
    });
    return !alreadyAdopted;
  }, [authToken]);

  const addCustomGoal = useCallback((title: string, description: string, steps: CustomStep[]): string => {
    const goalId = `custom-goal-${Date.now()}`;
    const templateId = `custom-${Date.now()}`;
    const filteredSteps = steps.filter(s => s.label.trim());
    const newGoal: ActiveGoal = {
      id: goalId,
      templateId,
      adoptedAt: new Date().toISOString(),
      completedSteps: [],
      isCustom: true,
      customTitle: title.trim(),
      customDescription: description.trim() || undefined,
      customSteps: filteredSteps,
    };
    setGoals(prev => {
      const next = [...prev, newGoal];
      saveGoalsLocal(next);
      return next;
    });

    // Persist to API
    if (authToken) {
      apiRequest<{ id: number }>('POST', '/goals', authToken, {
        templateId,
        isCustom: true,
        customTitle: title.trim(),
        customDescription: description.trim() || null,
        customSteps: filteredSteps,
      }).then(result => {
        if (result) {
          setGoals(p => {
            const updated = p.map(g => g.id === goalId ? { ...g, serverId: result.id } : g);
            saveGoalsLocal(updated);
            return updated;
          });
        }
      });
    }

    return goalId;
  }, [authToken]);

  const toggleStep = useCallback(
    (goalId: string, stepId: string, template: GoalTemplate | null, customSteps?: CustomStep[]): { nowComplete: boolean } => {
      let nowComplete = false;
      setGoals(prev => {
        const next = prev.map(g => {
          if (g.id !== goalId) return g;
          const steps = g.completedSteps.includes(stepId)
            ? g.completedSteps.filter(s => s !== stepId)
            : [...g.completedSteps, stepId];
          const microSteps = g.isCustom
            ? (g.customSteps ?? customSteps ?? [])
            : (template?.microSteps ?? []);
          const allDone = microSteps.length > 0 && microSteps.every(ms => steps.includes(ms.id));
          if (allDone && !g.completedAt) nowComplete = true;
          const updatedGoal = {
            ...g,
            completedSteps: steps,
            completedAt: allDone ? new Date().toISOString() : g.completedAt,
          };

          // Persist step update to API
          if (authToken && g.serverId) {
            apiRequest('PUT', `/goals/${g.serverId}/steps`, authToken, {
              completedSteps: updatedGoal.completedSteps,
              completedAt: updatedGoal.completedAt ?? null,
            });
          }

          return updatedGoal;
        });
        saveGoalsLocal(next);
        return next;
      });
      return { nowComplete };
    },
    [authToken]
  );

  const markXPAwarded = useCallback((goalId: string) => {
    setGoals(prev => {
      const next = prev.map(g => {
        if (g.id !== goalId) return g;

        // Persist to API
        if (authToken && g.serverId) {
          apiRequest('PUT', `/goals/${g.serverId}/xp`, authToken, {});
        }

        return { ...g, xpAwarded: true };
      });
      saveGoalsLocal(next);
      return next;
    });
  }, [authToken]);

  const activeGoals = goals.filter(g => !g.completedAt);
  const completedGoals = goals.filter(g => !!g.completedAt);
  const totalAdopted = goals.length;
  const totalCompleted = completedGoals.length;

  return {
    goals,
    activeGoals,
    completedGoals,
    adoptGoal,
    addCustomGoal,
    toggleStep,
    markXPAwarded,
    totalAdopted,
    totalCompleted,
  };
}
